import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/lib/queryKeys';
import {
  fetchGateway, saveGateway, testConnection,
  fetchTemplates, createTemplate, updateTemplate, deactivateTemplate,
  fetchDeliveryLog, fetchEvents,
} from './api';
import { fetchDeliveryLog as fetchWhatsAppDeliveryLog, fetchEvents as fetchWhatsAppEvents } from '../whatsapp-gateway/api';
import type { SmsGatewaySettings, SmsTemplateInput, DeliveryLogFilters } from './types';

/** `enabled: false` for a viewer without the admin:sms-gateway grant (the shared Notifications tab). */
export function useGateway({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: keys.admin.smsGateway(), queryFn: fetchGateway, enabled });
}

/**
 * Saving the account re-reads the readiness verdict, and the templates too:
 * turning a switch on changes what the Templates tab's per-wording badges say
 * about whether a send would actually go out.
 */
export function useSaveGateway() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<SmsGatewaySettings> & { auth_key?: string }) => saveGateway(data),
    onSuccess: (data) => {
      qc.setQueryData(keys.admin.smsGateway(), data);
      qc.invalidateQueries({ queryKey: keys.admin.smsTemplates() });
    },
  });
}

/** Save & test connection — the save is part of the action, see api.ts. */
export function useTestConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<SmsGatewaySettings> & { auth_key?: string }) => testConnection(data),
    onSettled: () => { qc.invalidateQueries({ queryKey: keys.admin.smsGateway() }); },
  });
}

/** `enabled: false` for a viewer without the admin:sms-gateway grant (the shared Delivery log). */
export function useTemplates({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: keys.admin.smsTemplates(), queryFn: fetchTemplates, enabled });
}

export function useSaveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id?: number; data: SmsTemplateInput }) =>
      (id ? updateTemplate(id, data) : createTemplate(data)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.admin.smsTemplates() }); },
  });
}

export function useDeactivateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deactivateTemplate(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.admin.smsTemplates() }); },
  });
}

/**
 * The messages the app sends by itself. Static (it is code, not data), so it
 * is fetched once. `via` picks the endpoint this viewer is granted, as for
 * the delivery log below.
 */
export function useAutomaticEvents({ via = 'sms' }: { via?: 'sms' | 'whatsapp' } = {}) {
  return useQuery({
    queryKey: keys.admin.notificationEvents(),
    queryFn: via === 'whatsapp' ? fetchWhatsAppEvents : fetchEvents,
    staleTime: Infinity,
  });
}

/**
 * The one delivery log (SMS and WhatsApp share notification_log and its
 * query). `via` only picks which endpoint answers — the one this viewer is
 * granted — so a WhatsApp-only admin still sees the log.
 */
export function useDeliveryLog(filters: DeliveryLogFilters, { via = 'sms' }: { via?: 'sms' | 'whatsapp' } = {}) {
  return useQuery({
    queryKey: keys.admin.smsLog({ ...filters, via } as Record<string, unknown>),
    queryFn: () => (via === 'whatsapp' ? fetchWhatsAppDeliveryLog(filters) : fetchDeliveryLog(filters)),
  });
}
